import {
  ApplicationConfig,
  ErrorHandler,
  LOCALE_ID,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import localeFi from '@angular/common/locales/fi';
import { provideRouter, withInMemoryScrolling, withNavigationErrorHandler } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import * as Sentry from '@sentry/angular';
import { provideTanStackQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { routes } from './app.routes';
import { missingWebshopSessionInterceptor } from './core/http-interceptors/missing-webshop-session-interceptor';
import { recoverFromChunkLoadFailure } from './core/monitoring/chunk-load-recovery';
import { sentryErrorExtractor } from './core/monitoring/sentry-http-error';
import { environment } from '../environments/environment';

registerLocaleData(localeFi);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      routes,
      withInMemoryScrolling({ scrollPositionRestoration: 'top' }),
      // Every route is a `loadComponent`, so a deploy that removes the chunk a
      // long-open tab is about to fetch surfaces here. See
      // `chunk-load-recovery.ts` for why that ends in a blank screen rather than
      // a 404. Returning nothing (no `RedirectCommand`) leaves the router's own
      // error handling untouched, so anything this does not recognise still
      // reaches the ErrorHandler and Sentry.
      withNavigationErrorHandler((event) => {
        recoverFromChunkLoadFailure(event.error);
      }),
    ),
    provideAnimationsAsync(),
    provideHttpClient(withXhr(), withInterceptors([missingWebshopSessionInterceptor])),
    provideTanStackQuery(queryClient),
    { provide: LOCALE_ID, useValue: 'fi' },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
        // The SDK's default `logErrors: true` re-`console.error`s every error it
        // has just captured, and `consoleLoggingIntegration`
        // turns that echo into a Sentry *Log* alongside the Issue — the same
        // failure ingested twice on two different surfaces. Kept on where Sentry
        // is off (local dev), because there the echo is the only place an
        // uncaught error would show up at all.
        logErrors: !environment.sentry.enabled,
        // Turns this backend's `{ data, name, statusCode }` failures into errors
        // that group by endpoint and status instead of collapsing into one
        // `<unknown>` issue. See `sentry-http-error.ts`.
        extractor: sentryErrorExtractor,
      }),
    },
  ],
};
